import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, Download, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface CommissionReportItem {
  id: string;
  referrer_name: string;
  referrer_type: string;
  total_referrals: number;
  total_service_amount: number;
  total_commission: number;
  paid_commission: number;
  pending_commission: number;
}

export default function CommissionReports() {
  const { formatCurrency } = useCurrency();
  const [reportData, setReportData] = useState<CommissionReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadCommissionReports();
  }, [startDate, endDate, statusFilter]);

  const loadCommissionReports = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setReportData(getMockCommissionReports());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('referral_accruals')
        .select(`
          id,
          referrer_name,
          service_amount,
          fee_amount,
          status,
          service_date,
          contract:referral_contracts(referrer_type)
        `);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      if (startDate) {
        query = query.gte('service_date', startDate);
      }
      if (endDate) {
        query = query.lte('service_date', endDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      const grouped = (data || []).reduce((acc: Record<string, any>, item) => {
        const referrerKey = item.referrer_name;
        if (!acc[referrerKey]) {
          acc[referrerKey] = {
            id: item.id,
            referrer_name: item.referrer_name,
            referrer_type: item.contract?.referrer_type || 'N/A',
            total_referrals: 0,
            total_service_amount: 0,
            total_commission: 0,
            paid_commission: 0,
            pending_commission: 0,
          };
        }
        
        acc[referrerKey].total_referrals += 1;
        acc[referrerKey].total_service_amount += item.service_amount;
        acc[referrerKey].total_commission += item.fee_amount;
        if (item.status === 'paid') {
          acc[referrerKey].paid_commission += item.fee_amount;
        } else {
          acc[referrerKey].pending_commission += item.fee_amount;
        }
        
        return acc;
      }, {});

      setReportData(Object.values(grouped));
    } catch (error) {
      console.error('Error loading commission reports:', error);
      setReportData(getMockCommissionReports());
    } finally {
      setLoading(false);
    }
  };

  const getMockCommissionReports = (): CommissionReportItem[] => {
    return [
      {
        id: '1',
        referrer_name: 'Dr. Ahmed Rahman',
        referrer_type: 'doctor',
        total_referrals: 45,
        total_service_amount: 225000,
        total_commission: 22500,
        paid_commission: 22500,
        pending_commission: 0,
      },
      {
        id: '2',
        referrer_name: 'City Diagnostic Center',
        referrer_type: 'organization',
        total_referrals: 38,
        total_service_amount: 190000,
        total_commission: 28500,
        paid_commission: 15000,
        pending_commission: 13500,
      },
      {
        id: '3',
        referrer_name: 'Dr. Fatima Khan',
        referrer_type: 'doctor',
        total_referrals: 52,
        total_service_amount: 260000,
        total_commission: 26000,
        paid_commission: 26000,
        pending_commission: 0,
      },
    ];
  };

  const filteredData = reportData.filter(item => {
    const matchesSearch = 
      item.referrer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.referrer_type.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalCommission = filteredData.reduce((sum, item) => sum + item.total_commission, 0);
  const totalPaid = filteredData.reduce((sum, item) => sum + item.paid_commission, 0);
  const totalPending = filteredData.reduce((sum, item) => sum + item.pending_commission, 0);

  const handleExportCSV = () => {
    const headers = ['Referrer Name', 'Type', 'Total Referrals', 'Service Amount', 'Total Commission', 'Paid', 'Pending'];
    const rows = filteredData.map(item => [
      item.referrer_name,
      item.referrer_type,
      item.total_referrals,
      item.total_service_amount,
      item.total_commission,
      item.paid_commission,
      item.pending_commission,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission-reports-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Commission Reports</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Commission</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{formatCurrency(totalCommission)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Paid Commission</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalPaid)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Pending Commission</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{formatCurrency(totalPending)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search referrer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referrer Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referrals
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pending
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      No commission data found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.referrer_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {item.referrer_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.total_referrals}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.total_service_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-bold">
                        {formatCurrency(item.total_commission)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        {formatCurrency(item.paid_commission)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                        {formatCurrency(item.pending_commission)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
